import React, { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Button,
    Typography,
    Box,
    Alert,
    AlertTitle,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    ForwardToInbox,
    ContentCopy,
    Delete,
    CheckCircle,
    Add,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { emailAddressApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import LoadingButton from './LoadingButton';

const EmailForwardingSettings = () => {
    const [emailAddress, setEmailAddress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const { showSuccess, showError } = useNotification();

    useEffect(() => {
        loadEmailAddress();
    }, []);

    const loadEmailAddress = async () => {
        try {
            setLoading(true);
            const response = await emailAddressApi.get();
            // Backend returns the entire object with emailAddress, isActive, etc.
            setEmailAddress(response.data);
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Error loading email address:', error);
                showError('Failed to load email address');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        try {
            setGenerating(true);
            await emailAddressApi.generate();
            showSuccess('Forwarding email address generated successfully!');
            // Reload data to update UI
            await loadEmailAddress();
        } catch (error) {
            console.error('Error generating email:', error);
            showError(error.response?.data?.error || 'Failed to generate email address');
        } finally {
            setGenerating(false);
        }
    };

    const handleCopyEmail = () => {
        if (emailAddress?.emailAddress) {
            navigator.clipboard.writeText(emailAddress.emailAddress);
            showSuccess('Email address copied to clipboard');
        }
    };

    const handleDeactivate = async () => {
        if (!window.confirm('Are you sure you want to deactivate this email address? You will no longer receive emails at this address.')) {
            return;
        }

        try {
            setDeactivating(true);
            await emailAddressApi.delete();
            showSuccess('Email address deactivated successfully');
            // Reload data to update UI
            await loadEmailAddress();
        } catch (error) {
            console.error('Error deactivating email:', error);
            showError(error.response?.data?.error || 'Failed to deactivate email address');
        } finally {
            setDeactivating(false);
        }
    };

    if (loading) {
        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography>Loading email forwarding settings...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                avatar={<ForwardToInbox color={emailAddress?.isActive ? 'success' : 'disabled'} />}
                title="Your forwarding address"
                subheader="Forward bank emails here: they are read automatically and wait for your review"
                action={
                    emailAddress?.isActive ? (
                        <Chip label="Active" color="success" size="small" />
                    ) : (
                        <Chip label="Inactive" color="default" size="small" />
                    )
                }
            />
            <CardContent>
                {!emailAddress || !emailAddress.isActive ? (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Get your own PinePocket address, then forward your bank's alert emails to it. Each email is read automatically: transactions wait for your review, and card statements become bills.
                        </Alert>

                        <LoadingButton
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleGenerate}
                            loading={generating}
                        >
                            Generate Forwarding Email
                        </LoadingButton>
                    </Box>
                ) : (
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            <Typography variant="body2" gutterBottom>
                                Your forwarding email is active! Forward bank emails to this address.
                            </Typography>
                            {emailAddress.lastEmailReceivedAt && (
                                <Typography variant="caption" color="text.secondary">
                                    Last email received: {new Date(emailAddress.lastEmailReceivedAt).toLocaleString()}
                                </Typography>
                            )}
                        </Alert>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Your Forwarding Email Address:
                            </Typography>
                            <TextField
                                fullWidth
                                value={emailAddress.emailAddress}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <Tooltip title="Copy to clipboard">
                                            <IconButton onClick={handleCopyEmail} edge="end">
                                                <ContentCopy />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                        </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Setup Instructions
                </Typography>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            <strong>Step 1:</strong> Open Gmail Settings
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" paragraph>
                            1. Open <a href="https://mail.google.com" target="_blank" rel="noopener noreferrer">Gmail</a>
                        </Typography>
                        <Typography variant="body2" paragraph>
                            2. Click the gear icon (⚙️) in the top right
                        </Typography>
                        <Typography variant="body2">
                            3. Click "See all settings"
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            <strong>Step 2:</strong> Set Up Forwarding & Verify
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" paragraph>
                            1. Go to the "Forwarding and POP/IMAP" tab
                        </Typography>
                        <Typography variant="body2" paragraph>
                            2. Click "Add a forwarding address"
                        </Typography>
                        <Typography variant="body2" paragraph>
                            3. Enter: <strong>{emailAddress.emailAddress}</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                            4. Click "Next" → "Proceed" → "OK"
                        </Typography>
                        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="body2" gutterBottom>
                                <strong>Verification Required:</strong> Gmail will send a confirmation email with a verification link.
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>To verify:</strong>
                            </Typography>
                            <Typography variant="body2" component="div">
                                1. Go to <strong>Email → Forwarded emails</strong> in PinePocket<br />
                                2. Click "Check for New Emails" to sync<br />
                                3. Find the email from Gmail Team (with lots of encoded text)<br />
                                4. Click to view the email<br />
                                5. Look for the verification URL in the message (it will look like: https://mail.google.com/mail/...)<br />
                                6. Copy and paste that URL in your browser to verify<br />
                                7. Return to Gmail settings and select your forwarding address
                            </Typography>
                        </Alert>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                            Note: We don't have a dedicated email viewer yet, so the Gmail confirmation email will appear as raw text with encoding. Just look for the https://mail.google.com URL.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                            <strong>Step 3:</strong> Create a Filter for Bank Emails
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" paragraph>
                            1. Go to Gmail Settings → "Filters and Blocked Addresses" tab
                        </Typography>
                        <Typography variant="body2" paragraph>
                            2. Click "Create a new filter"
                        </Typography>
                        <Typography variant="body2" paragraph>
                            3. In the "From" field, enter your bank's email address
                            <br />
                            Examples: *@icicibank.com, *@hdfcbank.com, *@sbi.co.in
                        </Typography>
                        <Typography variant="body2" paragraph>
                            4. Click "Create filter"
                        </Typography>
                        <Typography variant="body2" paragraph>
                            5. Check "Forward it to" and select <strong>{emailAddress.emailAddress}</strong>
                        </Typography>
                        <Typography variant="body2">
                            6. Click "Create filter"
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Alert severity="success" sx={{ mt: 3 }}>
                    <AlertTitle>Testing</AlertTitle>
                    Forward a test bank email to verify the setup. Check "Email" → "Forwarded emails" to see if it arrives.
                </Alert>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    <LoadingButton
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleDeactivate}
                        loading={deactivating}
                    >
                        Deactivate Email
                    </LoadingButton>
                </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default EmailForwardingSettings;

